import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import LoadingState from './LoadingState';
import {uiMessage} from './uiText';

const examples = ['帮我查看 GJB2 基因的变异配对网络', '查询 GJB2 基因的 c.109G>A 位点', '查找听力下降表型相关的基因'];

export default function IntentAssistant({ mode, version, unitId, contextKey, onReady, onManual,getView,helpRef }) {
    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [reply, setReply] = useState('');
    const [tone, setTone] = useState('info');
    const request = useRef(null);
    const sequence = useRef(0);
    const input = useRef(null);
    const executing=useRef(false),[running,setRunning]=useState(false);
    useEffect(() => () => { sequence.current += 1; request.current?.abort(); }, []);
    useEffect(() => { if(executing.current)return;sequence.current += 1; request.current?.abort(); setBusy(false); setReply(''); }, [version, unitId, contextKey]);
    function manual() {
        sequence.current += 1; request.current?.abort(); setBusy(false); setReply(''); setQuery(''); onManual();
    }
    async function submit() {
        if (!query.trim() || busy) return;
        const id = ++sequence.current;
        request.current?.abort(); const abort = new AbortController(); request.current = abort;
        setBusy(true); setReply(''); setTone('info');
        try {
            let response;
            for (let attempt = 1; attempt <= 3; attempt += 1) {
                try {
                    response = await axios.post('/api/rgnet/intent', { query: query.trim(), ...(version ? { datasetVersion: version } : {}), ...(unitId ? { unitId } : {}),...(getView?{view:getView()}: {}) }, {
                        headers: { Authorization: localStorage.getItem('userInfo') || '' }, signal: abort.signal, timeout: 70000,
                    });
                    break;
                } catch (ex) {
                    if (abort.signal.aborted || axios.isCancel(ex) || ex.response || ex.code === 'ECONNABORTED') throw ex;
                    if (attempt === 3) {
                        if (id === sequence.current) onManual('连续三次无法连接意图识别服务，已切换手动模式。');
                        return;
                    }
                }
            }
            if (abort.signal.aborted || id !== sequence.current) return;
            if (response.data.code !== 200) throw new Error(response.data.msg || '意图识别服务不可用');
            const data = response.data.data;
            setReply(uiMessage(data.message,'请补充或调整查询条件。'));
            setTone(data.status === 'ready' ? 'success' : 'warning');
            if (data.status === 'ready') {
                executing.current=true;setRunning(true);
                try {
                    const result=await onReady(data,abort.signal);
                    if(!abort.signal.aborted&&id===sequence.current){
                        const message=typeof result==='string'?result:result?.message;
                        if(message)setReply(message);
                        if(result?.completed!==false)setQuery('');
                    }
                }finally{executing.current=false;setRunning(false);}
            }
            else if (data.status === 'manual') { setReply(''); onManual(uiMessage(data.message,'意图识别未完成，已切换手动模式。')); }
        } catch (ex) {
            if (abort.signal.aborted || id !== sequence.current || axios.isCancel(ex)||ex.name==='AbortError') return;
            setTone('error'); setReply(ex.response?.status === 401 ? '登录已过期，请重新登录。' : /[\u3400-\u9fff]/.test(ex.message||'')?ex.message:'无法连接查询服务，请重试或使用手动模式。');
        } finally { if (!abort.signal.aborted && id === sequence.current) setBusy(false); }
    }
    const content = <div className="rgnet-intent-content" aria-busy={busy}>
        {mode === 'entry' && <><div className="rgnet-eyebrow">隐性基因型网络</div><h2>查询 RGNET</h2></>}
        <p className="rgnet-intent-guide"><strong>用一句话描述查询需求</strong>，支持疾病、基因、位点、表型查询，也可继续筛选、对比或保存查询。</p>
        <div className="rgnet-intent-search"><Input ref={input} id="rgnet-intent-query" aria-label="查询需求" value={query} onChange={event => setQuery(event.target.value)}
            prefix={<SearchOutlined />} size="large" placeholder="例如：帮我查看 GJB2 基因的变异配对网络" maxLength={2000} disabled={busy}
            onPressEnter={submit} allowClear /><Button aria-label="查询" type="primary" size="large" loading={busy} disabled={busy || !query.trim()} onClick={submit}>查询</Button></div>
        {busy && <LoadingState title={running?'正在执行…':'正在查询…'} description={running?'请等待当前操作完成。':'正在匹配数据库中的疾病、基因和位点。'} timed />}
        <details ref={helpRef} className="rgnet-intent-help" open={mode==='entry'?true:undefined}><summary>查询示例</summary><div className="rgnet-intent-examples" aria-label="自然语言查询示例"><span>试着问：</span>{examples.map(example => <button type="button" key={example} disabled={busy} onClick={() => { setQuery(example); input.current?.focus({ cursor: 'end' }); }}>{example}</button>)}</div><p className="rgnet-caption">例如：“只看 P 级”“切到网络记录”“下载 PNG”“保存当前查询为听力查询”。未指定的条件保持不变。</p></details>
        <div className="rgnet-intent-actions">
            <Button type="link" onClick={manual}>{mode === 'manual' ? '清空手动查询' : '使用手动模式'}</Button></div>
        {reply && <Alert type={tone} showIcon message={reply} />}
    </div>;
    return <section className={`rgnet-panel ${mode === 'entry' ? 'rgnet-intent-entry' : 'rgnet-intent-inline'}`}
        aria-label={mode === 'entry' ? 'RGNET 查询入口' : 'RGNET 查询助手'}>{content}</section>;
}
